<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="configuration-preview" :class="{ expanded }" @contextmenu.prevent="!expanded && openActionsMenu($event)">
    <div v-if="expanded" class="actions-menu">
      <v-menu bottom left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-for="(action, i) in configurationActions" :key="i" @click="action.action">
            <v-list-item-title>{{ action.label }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="title">{{ proactiveChatConfiguration.description }}</div>
    <div class="business-process">{{ businessProcessName }}</div>

    <div class="configuration-option">
      <div class="config inline">
        <div class="config-label">{{ $t('welcomeMessage') }}</div>
        <div class="config-value">{{ proactiveChatConfiguration.welcomeMessage }}</div>
      </div>
    </div>

    <div class="configuration-option">
      <div class="config inline">
        <div class="config-label">{{ $t('phone') }}</div>
        <div class="config-value">{{ proactiveChatConfiguration.phone }}</div>
      </div>
    </div>

    <div class="configuration-option">
      <div class="config inline">
        <div class="config-label">{{ $t('email') }}</div>
        <div class="config-value">{{ proactiveChatConfiguration.email }}</div>
      </div>
    </div>

    <div class="configuration-option">
      <div class="config">
        <div class="config-label">Show an offer message after</div>
        <div class="config-value">{{ delayedStartValue }}</div>
      </div>
    </div>
    <div class="configuration-option">
      <div class="config inline">
        <div class="config-label">{{ $t('offerMessage') }}</div>
        <div class="config-value">{{ proactiveChatConfiguration.offerMessage }}</div>
      </div>
    </div>
    <div class="configuration-option">
      <div class="config inline">
        <div class="config-label">{{ $t('timeZone') }}</div>
        <div class="config-value">
          {{ getWindowsTimeZone(proactiveChatConfiguration.ianaTimeZone) }} <br />
          <span class="current-time"> {{ $t('currentTime') }} {{ currentTime }} </span>
        </div>
      </div>
    </div>
    <div v-show="proactiveChatConfiguration.holidays.length" class="configuration-option">
      <div class="inline-title">Holidays</div>
      <div
        v-for="(holiday, index) in proactiveChatConfiguration.holidays"
        :key="index"
        class="schedule"
        v-show="holiday.schedule[0].from !== undefined"
      >
        <div class="schedule-day">
          {{ holiday.name }}<br />
          {{ formatHoliday(holiday.date) }}
        </div>
        <div class="schedule-time">
          {{ formatTime(holiday.schedule[0].from) }} - {{ formatTime(holiday.schedule[0].to) }}<br />
          <span v-show="holiday.schedule[1].from !== undefined">
            {{ formatTime(holiday.schedule[1].from) }} - {{ formatTime(holiday.schedule[1].to) }}<br />
          </span>
        </div>
      </div>
    </div>
    <div v-show="Object.entries(proactiveChatConfiguration.workingHrsSchedule).length" class="configuration-option">
      <div class="inline-title">Working Hrs Schedule</div>
      <div
        v-for="[day, schedule] in Object.entries(proactiveChatConfiguration.workingHrsSchedule)"
        :key="day"
        class="schedule"
        v-show="schedule[0].from !== undefined"
      >
        <div class="schedule-day">{{ $t(`schedule.${day}`) }}</div>
        <div class="schedule-time">
          {{ formatTime(schedule[0].from) }} - {{ formatTime(schedule[0].to) }}<br />
          <span v-show="schedule[1].from">
            {{ formatTime(schedule[1].from) }} - {{ formatTime(schedule[1].to) }}<br />
          </span>
        </div>
      </div>
    </div>
    <v-btn tile @click="copySnippet" class="link snippet-btn">
      Copy code snippet
      <v-spacer />
      <v-icon small right>mdi-content-copy</v-icon>
    </v-btn>
  </div>
</template>

<script>
import moment from 'moment';
import { ProactiveChatConfigurations } from '@/services';
import { mapActions, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { snippetBuilder } from '@/components/ecs-proactive-chat-configuration-preview/customer-snippet.builder';
import { IanaToWindowsTimeZones } from '../ecs-configuration-wizard-dialog/windowsTimeZones';
import DateTime from 'luxon/src/datetime';

export default {
  name: 'EcsProactiveChatConfigurationPreview',

  components: {},

  props: {
    expanded: {
      type: Boolean,
      default: false
    },
    proactiveChatConfiguration: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      delayedStartOptions: [
        {
          label: 'Never',
          value: -1
        },
        {
          label: 'Instantly',
          value: 0
        },
        {
          label: '15 Seconds',
          value: 15
        },
        {
          label: '30 Seconds',
          value: 30
        },
        {
          label: '1 Minute',
          value: 60
        },
        {
          label: '3 Minutes',
          value: 60 * 3
        },
        {
          label: '5 Minutes',
          value: 60 * 5
        }
      ],
      configurationActions: [
        {
          label: 'Edit',
          action: () => {
            this.updateConfiguration(this.proactiveChatConfiguration);
          }
        },
        {
          label: 'Duplicate',
          action: () => {
            this.duplicateConfiguration(this.proactiveChatConfiguration);
          }
        },
        {
          label: 'Delete',
          action: () => {
            this.$root.$emit('open-confirm-dialog', {
              title: this.$t('actions.delete.confirm.title'),
              message: this.$t('actions.delete.confirm.message', { name: this.proactiveChatConfiguration.description }),
              action: this.deleteConfiguration
            });
          }
        }
      ],
      currentTime: '',
      currentTimeIntervalRef: ''
    };
  },

  computed: {
    ...mapGetters(['businessProcesses', 'organizationId', 'chatBaseUrl']),

    delayedStartValue() {
      return this.delayedStartOptions.find(
        option => option.value === this.proactiveChatConfiguration.openChatOfferAfter
      ).label;
    },

    businessProcessName() {
      const bp = this.businessProcesses.find(bp => bp.id === this.proactiveChatConfiguration.businessProcessId);
      return bp ? bp.name : this.proactiveChatConfiguration.businessProcessId;
    }
  },

  created() {
    this.currentTimeIntervalRef = setInterval(this.updateCurrentTime, 1000);
  },

  beforeDestroy() {
    clearInterval(this.currentTimeIntervalRef);
  },

  methods: {
    ...mapActions(['snackbarSuccessMessage']),

    updateCurrentTime() {
      this.currentTime = new Date().toLocaleTimeString('en-US', {
        timeZone: this.proactiveChatConfiguration.ianaTimeZone
      });
    },

    getWindowsTimeZone(ianaTimeZone) {
      if (this.proactiveChatConfiguration.msTimeZone) {
        return this.proactiveChatConfiguration.msTimeZone;
      }

      const dt = DateTime.local().setZone(ianaTimeZone);
      const isDST = dt.isInDST;
      return isDST ? `${IanaToWindowsTimeZones[ianaTimeZone]} DST` : IanaToWindowsTimeZones[ianaTimeZone];
    },

    formatTime(minutes) {
      return moment.utc(minutes * 60 * 1000).format('HH:mm');
    },

    formatHoliday(date) {
      return moment(date).format('MMM Do');
    },

    updateConfiguration(configuration) {
      this.$root.$emit('open-configuration-dialog', { configuration, callback: this.saveUpdatedConfiguration });
    },

    duplicateConfiguration(configuration) {
      const duplicatedConfig = cloneDeep(configuration);
      delete duplicatedConfig._id;
      duplicatedConfig.description = `${duplicatedConfig.description} (Copy)`;

      this.$root.$emit('open-configuration-dialog', {
        configuration: duplicatedConfig,
        callback: this.saveDuplicatedConfiguration
      });
    },

    saveDuplicatedConfiguration(configuration) {
      if (configuration) {
        ProactiveChatConfigurations.createConfiguration(configuration)
          .then(() => {
            this.snackbarSuccessMessage(
              this.$t('actions.create.success', { name: this.proactiveChatConfiguration.description })
            );
            this.$root.$emit('update-proactive-chat-configurations');
          })
          .catch(() => {
            console.log('Error while creating', configuration.description);
          });
      }
    },

    saveUpdatedConfiguration(updatedConfiguration) {
      if (updatedConfiguration) {
        ProactiveChatConfigurations.updateConfiguration(updatedConfiguration._id, updatedConfiguration)
          .then(() => {
            this.snackbarSuccessMessage(
              this.$t('actions.update.success', { name: this.proactiveChatConfiguration.description })
            );
            this.$root.$emit('update-proactive-chat-configurations');
          })
          .catch(() => {
            this.$t('actions.update.error', { name: this.proactiveChatConfiguration.description });
          });
      }
    },

    deleteConfiguration(confirmed) {
      if (confirmed) {
        return ProactiveChatConfigurations.deleteConfiguration(this.proactiveChatConfiguration._id).then(() => {
          this.snackbarSuccessMessage(
            this.$t('actions.delete.success', { name: this.proactiveChatConfiguration.description })
          );
          this.$root.$emit('update-proactive-chat-configurations');
        });
      } else {
        return Promise.resolve();
      }
    },

    copySnippet() {
      const baseSetup = {
        baseUrl: this.chatBaseUrl,
        proactiveChatConfigAPI: `${new URL(window.location.href).origin}/api/proactive-chat-configurations`,
        businessProcessEndPoint: this.proactiveChatConfiguration.businessProcessEndPoint,
        orgId: this.proactiveChatConfiguration.orgId,
        configId: this.proactiveChatConfiguration._id,
        customer: this.proactiveChatConfiguration.defaultCustomer
      };

      navigator.clipboard
        .writeText(snippetBuilder(JSON.stringify(baseSetup), JSON.stringify(this.proactiveChatConfiguration)))
        .then(() => {
          this.snackbarSuccessMessage('The code snippet was successfully copied to your Clipboard');
        })
        .catch(() => {});
    },

    openActionsMenu(event) {
      this.$root.$emit('open-context-menu', {
        menu: this.configurationActions,
        position: {
          x: event.clientX,
          y: event.clientY
        }
      });
    }
  }
};
</script>

<style scoped src="./scoped.scss" lang="scss" />
